import { Fragment, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Boxes, ChevronRight, Download } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  PageHeaderSkeleton,
  StatCardsSkeleton,
  TableSkeleton,
} from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useConsumptionBySku } from "~/hooks/costing";
import { exportCsv } from "~/utils/csv";
import type { ConsumptionBySkuRow } from "~/types/costing";

import {
  ITEM_KIND_LABELS,
  PeriodSelector,
  fmtCLP,
  fmtQty,
  useCosteoPeriod,
} from "./components/CosteoShared";

type ViewMode = "ingredient" | "sku";

/** One (ingredient × product) consumption line, with oils converted to kg and
 *  per-unit / recipe ratios derived for display. */
interface FlatRow {
  key: string;
  ingredientId: string;
  ingredientCode: string;
  ingredientName: string;
  itemKind: "raw_material" | "oil_type";
  unit: string;
  productId: string;
  productSku: string;
  productName: string;
  productionUnit: string;
  qty: number;
  cost: number;
  boxes: number;
  realPerBox: number | null;
  recipePerBox: number | null;
  variancePct: number | null;
}

interface Group {
  key: string;
  title: string;
  subtitle: string;
  itemKind?: "raw_material" | "oil_type";
  unit: string;
  totalQty: number;
  totalCost: number;
  boxes?: number;
  productionUnit?: string;
  children: FlatRow[];
}

/** Ratios: up to 4 significant digits so both 16.88 kg/caja and 0.00025 read well. */
const fmtRatio = (v: number): string =>
  new Intl.NumberFormat("es-CL", { maximumSignificantDigits: 4 }).format(v);

function VarianceCell({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-muted-foreground">—</span>;
  const rounded = Math.round(pct * 10) / 10;
  const cls =
    Math.abs(rounded) < 1
      ? "text-muted-foreground"
      : rounded > 0
        ? "text-red-700"
        : "text-green-700";
  return (
    <span className={`font-medium ${cls}`}>
      {rounded > 0 ? "+" : ""}
      {rounded.toFixed(1)}%
    </span>
  );
}

function enrich(rows: ConsumptionBySkuRow[]): FlatRow[] {
  return rows.map((r) => {
    const isOil = r.item_kind === "oil_type";
    const density = isOil ? Number(r.density_kg_per_liter) || 1 : 1;
    const unit = isOil ? "kg" : r.ingredient_unit || "u";
    const qty = Number(r.quantity) * density;
    const cost = Number(r.total_cost_clp);
    const boxes = Number(r.boxes_produced) || 0;
    const recipePerBox =
      r.recipe_qty_per_box == null ? null : Number(r.recipe_qty_per_box) * density;
    const realPerBox = boxes > 0 ? qty / boxes : null;
    const variancePct =
      realPerBox != null && recipePerBox != null && recipePerBox !== 0
        ? ((realPerBox - recipePerBox) / recipePerBox) * 100
        : null;
    const ingredientId = r.raw_material_id ?? r.oil_type_id ?? "";
    return {
      key: `${ingredientId}__${r.product_id}`,
      ingredientId,
      ingredientCode: r.ingredient_code ?? "",
      ingredientName: r.ingredient_name ?? "",
      itemKind: r.item_kind,
      unit,
      productId: r.product_id,
      productSku: r.product_sku ?? "",
      productName: r.product_name ?? "",
      productionUnit: r.production_unit ?? "CAJA",
      qty,
      cost,
      boxes,
      realPerBox,
      recipePerBox,
      variancePct,
    };
  });
}

export function ConsumoPorSkuPage() {
  const navigate = useNavigate();
  const { periods, period, setPeriod } = useCosteoPeriod();
  const { data: rows = [], isLoading } = useConsumptionBySku(period);

  const [view, setView] = useState<ViewMode>("ingredient");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const flat = useMemo(() => enrich(rows), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flat;
    return flat.filter(
      (r) =>
        r.ingredientCode.toLowerCase().includes(q) ||
        r.ingredientName.toLowerCase().includes(q) ||
        r.productSku.toLowerCase().includes(q) ||
        r.productName.toLowerCase().includes(q)
    );
  }, [flat, search]);

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();
    for (const r of filtered) {
      if (view === "ingredient") {
        let g = map.get(r.ingredientId);
        if (!g) {
          g = {
            key: r.ingredientId,
            title: r.ingredientName,
            subtitle: `${r.ingredientCode} · ${ITEM_KIND_LABELS[r.itemKind]}`,
            itemKind: r.itemKind,
            unit: r.unit,
            totalQty: 0,
            totalCost: 0,
            children: [],
          };
          map.set(r.ingredientId, g);
        }
        g.totalQty += r.qty;
        g.totalCost += r.cost;
        g.children.push(r);
      } else {
        let g = map.get(r.productId);
        if (!g) {
          g = {
            key: r.productId,
            title: r.productName,
            subtitle: r.productSku,
            unit: "",
            totalQty: 0,
            totalCost: 0,
            boxes: r.boxes,
            productionUnit: r.productionUnit,
            children: [],
          };
          map.set(r.productId, g);
        }
        g.totalCost += r.cost;
        g.children.push(r);
      }
    }
    const arr = Array.from(map.values());
    for (const g of arr) g.children.sort((a, b) => b.cost - a.cost);
    arr.sort((a, b) => b.totalCost - a.totalCost);
    return arr;
  }, [filtered, view]);

  const totalCost = useMemo(
    () => filtered.reduce((s, r) => s + r.cost, 0),
    [filtered]
  );
  const nIngredients = useMemo(
    () => new Set(filtered.map((r) => r.ingredientId)).size,
    [filtered]
  );
  const nSkus = useMemo(
    () => new Set(filtered.map((r) => r.productId)).size,
    [filtered]
  );

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allExpanded = groups.length > 0 && expanded.size >= groups.length;
  const toggleAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(groups.map((g) => g.key)));

  const changeView = (v: ViewMode) => {
    setView(v);
    setExpanded(new Set());
  };

  const handleExport = () => {
    exportCsv(
      `consumo_por_sku_${period}.csv`,
      [
        "Tipo",
        "Código Insumo",
        "Insumo",
        "SKU",
        "Producto",
        "Cantidad",
        "Unidad",
        "Costo (CLP)",
        "Unidades Producidas",
        "Real por Unidad",
        "Receta por Unidad",
        "Diferencia %",
      ],
      filtered.map((r) => [
        ITEM_KIND_LABELS[r.itemKind],
        r.ingredientCode,
        r.ingredientName,
        r.productSku,
        r.productName,
        r.qty.toFixed(2),
        r.unit,
        Math.round(r.cost),
        r.boxes || "",
        r.realPerBox != null ? r.realPerBox.toFixed(4) : "",
        r.recipePerBox != null ? r.recipePerBox.toFixed(4) : "",
        r.variancePct != null ? r.variancePct.toFixed(1) : "",
      ])
    );
  };

  if (!period || isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <StatCardsSkeleton count={3} />
        <TableSkeleton rows={8} columns={4} />
      </PageContainer>
    );
  }

  const isSku = view === "sku";

  return (
    <PageContainer>
      <PageHeader
        title="Consumo por SKU"
        description="Consumo de materias primas y aceites atribuido a cada producto (SKU), valorizado al costo promedio (WAC) del período."
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/production/consumos")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <>
            <PeriodSelector
              periods={periods}
              value={period}
              onChange={setPeriod}
            />
            <Button variant="outline" className="h-9" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Exportar CSV
            </Button>
          </>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">
              Costo total consumido
            </div>
            <div className="mt-1 text-2xl font-bold">{fmtCLP(totalCost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">
              Insumos (materias primas / aceites)
            </div>
            <div className="mt-1 text-2xl font-bold">{nIngredients}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">
              SKUs producidos
            </div>
            <div className="mt-1 text-2xl font-bold">{nSkus}</div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar: view toggle + search + expand-all */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Tabs value={view} onValueChange={(v) => changeView(v as ViewMode)}>
          <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
            <TabsTrigger value="ingredient">Por Materia Prima</TabsTrigger>
            <TabsTrigger value="sku">Por SKU</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar insumo o producto…"
          className="h-9 w-full sm:max-w-xs"
        />
        {groups.length > 0 && (
          <Button
            variant="outline"
            className="h-9 sm:ml-auto"
            onClick={toggleAll}
          >
            {allExpanded ? "Colapsar todo" : "Expandir todo"}
          </Button>
        )}
      </div>

      {/* Grouped table */}
      {groups.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title={`Sin consumos registrados en ${period}`}
          description="Los consumos aparecen aquí una vez que se sincronizan los lotes de producción del período en Costeo."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isSku ? "Producto / Insumo" : "Insumo / Producto"}</TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Cantidad
                    </TableHead>
                    {isSku ? (
                      <>
                        <TableHead className="whitespace-nowrap text-right">
                          Real / u.
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          Receta / u.
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          Dif. %
                        </TableHead>
                      </>
                    ) : (
                      <TableHead className="whitespace-nowrap text-right">
                        % del insumo
                      </TableHead>
                    )}
                    <TableHead className="whitespace-nowrap text-right">
                      Costo
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => {
                    const isOpen = expanded.has(g.key);
                    return (
                      <Fragment key={g.key}>
                        <TableRow
                          className="cursor-pointer bg-muted/60 font-bold hover:bg-muted/70"
                          onClick={() => toggle(g.key)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ChevronRight
                                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                                  isOpen ? "rotate-90" : ""
                                }`}
                              />
                              <div className="min-w-0">
                                <div className="truncate">{g.title}</div>
                                <div className="text-xs font-normal text-muted-foreground">
                                  {g.subtitle}
                                  {isSku && g.boxes != null && (
                                    <>
                                      {" · "}
                                      {fmtQty(g.boxes)}{" "}
                                      {(g.productionUnit ?? "").toLowerCase() ||
                                        "u"}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right">
                            {isSku ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <>
                                {fmtQty(g.totalQty)}{" "}
                                <span className="text-xs font-normal text-muted-foreground">
                                  {g.unit}
                                </span>
                              </>
                            )}
                          </TableCell>
                          {isSku ? (
                            <>
                              <TableCell />
                              <TableCell />
                              <TableCell />
                            </>
                          ) : (
                            <TableCell className="text-right font-normal text-muted-foreground">
                              100%
                            </TableCell>
                          )}
                          <TableCell className="whitespace-nowrap text-right">
                            {fmtCLP(g.totalCost)}
                          </TableCell>
                        </TableRow>

                        {isOpen &&
                          g.children.map((c) => (
                            <TableRow
                              key={c.key}
                              className="duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/30"
                            >
                              <TableCell className="pl-10">
                                {isSku ? (
                                  <>
                                    <div className="font-medium">
                                      {c.ingredientName}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {c.ingredientCode} ·{" "}
                                      {ITEM_KIND_LABELS[c.itemKind]}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="font-medium">
                                      {c.productName}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {c.productSku}
                                    </div>
                                  </>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-right">
                                {fmtQty(c.qty)}{" "}
                                <span className="text-xs text-muted-foreground">
                                  {c.unit}
                                </span>
                              </TableCell>
                              {isSku ? (
                                <>
                                  <TableCell className="whitespace-nowrap text-right">
                                    {c.realPerBox != null ? (
                                      fmtRatio(c.realPerBox)
                                    ) : (
                                      <span className="text-muted-foreground">
                                        —
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-right text-muted-foreground">
                                    {c.recipePerBox != null
                                      ? fmtRatio(c.recipePerBox)
                                      : "—"}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-right">
                                    <VarianceCell pct={c.variancePct} />
                                  </TableCell>
                                </>
                              ) : (
                                <TableCell className="text-right text-muted-foreground">
                                  {g.totalQty > 0
                                    ? `${((c.qty / g.totalQty) * 100).toFixed(1)}%`
                                    : "—"}
                                </TableCell>
                              )}
                              <TableCell className="whitespace-nowrap text-right">
                                {fmtCLP(c.cost)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between gap-4 border-t bg-muted/30 px-4 py-2 text-sm">
              <span className="text-muted-foreground">
                {isSku ? `${nSkus} SKUs` : `${nIngredients} insumos`}
              </span>
              <span className="font-medium">
                Costo total: {fmtCLP(totalCost)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
